"use client"

import { useCallback } from "react"
import { authClient } from "@/lib/auth/auth-client"
import { trackService } from "./track-service"
import {
	getEventTypeProperties,
	TRACKING_CONFIG,
	TrackingEventType,
} from "./tracking-config"

// 自定义埋点 hook
export const useTracking = () => {
	const { data: session } = authClient.useSession()

	// 通用事件追踪函数 - 支持事件类型参数
	const track = useCallback(
		async (
			eventName: string,
			customProperties?: Record<string, any>,
			eventType?: TrackingEventType,
		) => {
			try {
				// 根据事件类型获取对应的公共属性
				const typeProperties = eventType
					? getEventTypeProperties(eventType)
					: {}

				// 合并公共属性和自定义属性
				const finalProperties = {
					...typeProperties,
					...customProperties,
				}

				// 使用新的埋点服务
				await trackService.trackEvent(eventName, finalProperties, session)
			} catch (error) {
				console.error("Track event failed:", error)
			}
		},
		[session],
	)

	// 页面曝光事件
	const trackPageView = useCallback(
		async (customProperties?: Record<string, any>) => {
			try {
				const viewProperties = getEventTypeProperties(TrackingEventType.VIEW)
				const finalProperties = {
					...viewProperties,
					...customProperties,
				}

				await trackService.trackPageView(finalProperties, session)
			} catch (error) {
				console.error("Track page view failed:", error)
			}
		},
		[session],
	)

	// 元素点击事件
	const trackClick = useCallback(
		async (customProperties?: Record<string, any>) => {
			try {
				const eventName = TRACKING_CONFIG.events.CLICK
				const clickProperties = getEventTypeProperties(TrackingEventType.CLICK)
				const finalProperties = {
					...clickProperties,
					...customProperties,
				}

				await trackService.trackEvent(eventName, finalProperties, session)
			} catch (error) {
				console.error("Track click failed:", error)
			}
		},
		[session],
	)

	// 页面离开事件（互动事件）
	const trackPageLeave = useCallback(
		async (customProperties?: Record<string, any>) => {
			try {
				const actionProperties = getEventTypeProperties(
					TrackingEventType.ACTION,
				)
				const finalProperties = {
					...actionProperties,
					...customProperties,
				}

				await trackService.trackPageLeave(finalProperties, session)
			} catch (error) {
				console.error("Track page leave failed:", error)
			}
		},
		[session],
	)

	return {
		track,
		isReady: true, // 新的埋点服务总是可用
		// 导出事件类型枚举供外部使用
		TrackingEventType,
		// 全埋点方法，仅供 global-tracker 使用
		_internal: {
			trackPageView,
			trackClick,
			trackPageLeave,
		},
	}
}

// 注意：页面浏览、点击、页面离开事件由 global-tracker 自动处理
// 业务组件只需要使用 useTracking().track() 方法进行自定义事件追踪
